import { Button, Form, Input, Spin } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LoadingOutlined,
  RightOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import { isMobile } from "react-device-detect";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactComponent as QuincyIcon } from "shared/assets/images/icon-quincy-pitching.svg";
import { ReactComponent as PencilIcon } from "shared/assets/images/pencil-icon.svg";
import {
  saveQuincyPlan,
  generateScheduleQuincy,
} from "shared/service/StrmService";
import { ReactComponent as QuincyLoadingIcon } from "shared/assets/images/quick-quincy-start-icon.svg";
import "explorer/pages/new-pitching/NewPitching.scss";
import "explorer/pages/new-pitching/steps/TextAreaQuincy.scss";
import "./new-releases-schedule-only-quincy.scss";
import { removeMarkdown } from "shared/helpers/string-helper";

const FIELD_CURRENT = "aICurrentPlan";
const FIELD_SUGGESTED = "aISuggestedPlan";

enum CurrentAction {
  None,
  Editing,
  Generating,
}

const TextAreaQuincySchedule: React.FC = () => {
  const { t } = useTranslation();
  const name = FIELD_CURRENT;
  const nameFieldSuggested = FIELD_SUGGESTED;
  const [currentAction, setCurrentAction] = useState<CurrentAction>(
    CurrentAction.None,
  );
  const [prompt, setPrompt] = useState("");
  const [previousSchedule, setPreviousSchedule] = useState("");

  const form = Form.useFormInstance();

  const onCancelEdit = () => {
    setCurrentAction(CurrentAction.None);
    form.setFieldValue(name, previousSchedule);
  };

  const onEdit = () => {
    setCurrentAction(CurrentAction.Editing);
    setPreviousSchedule(form.getFieldValue(name));
  };

  const onKeyDownPrompt = async (ev: React.KeyboardEvent) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      await regenerateQuincy();
    }
  };

  const onConfirmEdit = () => {
    setCurrentAction(CurrentAction.None);
  };

  const generateQuincy = async (): Promise<string> => {
    const res: any = await generateScheduleQuincy();
    const raw =
      res?.response?.schedulePlan ?? res?.schedulePlan ?? "";
    return removeMarkdown(raw);
  };

  const regenerateQuincy = async () => {
    if (currentAction === CurrentAction.Generating) return;

    setCurrentAction(CurrentAction.Generating);
    try {
      const response = await generateQuincy();
      form.setFieldValue(name, response);
      form.setFieldValue(nameFieldSuggested, response);
      setPrompt("");
    } finally {
      setCurrentAction(CurrentAction.None);
    }
  };

  return (
    <div className="wrapper-quincy">
      <Form.Item name={nameFieldSuggested} hidden>
        <Input />
      </Form.Item>
      <div className="header">
        {currentAction === CurrentAction.Editing ? (
          <button className="btn-cancel" onClick={onCancelEdit} type="button">
            <LeftOutlined />
          </button>
        ) : (
          <>
            <span className="icon">
              <QuincyIcon />
            </span>
            <span className="name">
              {t("pages.ReleasesSchedule.onlyQuincy.quincyIA")}
            </span>
          </>
        )}
      </div>
      <div className="body">
        {currentAction === CurrentAction.Generating && (
          <span className="quincy-loading">
            <Spin
              className="spin-loading"
              indicator={<LoadingOutlined spin />}
            />
            {t("pages.ReleasesSchedule.onlyQuincy.gerandoSeuCronograma")}
          </span>
        )}
        <Form.Item
          name={name}
          rules={[{ required: true }]}
          hidden={currentAction === CurrentAction.Generating}
        >
          <TextArea
            className={`textarea-quincy ${currentAction === CurrentAction.None ? "readonly" : ""
              }`}
            rows={12}
            readOnly={currentAction === CurrentAction.None}
          />
        </Form.Item>
        {currentAction !== CurrentAction.Generating && (
          <div className="div-count">
            {currentAction === CurrentAction.Editing && (
              <div>
                <Button className="botao-branco" onClick={onConfirmEdit}>
                  {t("pages.ReleasesSchedule.onlyQuincy.confirmar")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {currentAction === CurrentAction.None && (
        <div className="footer">
          <div className="buttons">
            <button className="round" onClick={onEdit} type="button">
              <PencilIcon />
            </button>
            <button
              className="btn-generate-pitching"
              type="button"
            >
              <QuincyIcon />
              <span>
                {t("pages.ReleasesSchedule.onlyQuincy.gerarNovamente")}
              </span>
            </button>
          </div>
          <div className="div-prompt">
            <Input
              className="prompt-chat"
              placeholder={t(
                "pages.ReleasesSchedule.onlyQuincy.quincyPlaceholder",
              )}
              suffix={
                <button
                  className="btn-prompt-icon"
                  type="button"
                  disabled={!prompt}
                >
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="input-prompt-icon"
                    color="#0D0D0D"
                  />
                </button>
              }
              onChange={(ev) => setPrompt(ev.target.value)}
              onKeyDown={onKeyDownPrompt}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const NewReleasesScheduleOnlyQuincy = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [form] = Form.useForm();
  const [loadingRecommendation, setLoadingRecommendation] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initialRecommendation, setInitialRecommendation] = useState("");

  const onSave = async () => {
    try {
      await form.validateFields([FIELD_CURRENT]);
      setSaving(true);
      const values = form.getFieldsValue();
      await saveQuincyPlan({
        aICurrentPlan: values[FIELD_CURRENT] ?? "",
        aISuggestedPlan: values[FIELD_SUGGESTED] ?? "",
      });
      toast.success<string>(t("pages.ReleasesSchedule.salvoSucesso"));
      history.push("/releases-schedules");
    } catch (err) {
      toast.error<string>(t("pages.ReleasesSchedule.erroAoSalvar"));
    } finally {
      setSaving(false);
    }
  };



  useEffect(() => {
    const onGenerate = async () => {
      const { response }: any = await generateScheduleQuincy();
      setInitialRecommendation(removeMarkdown(response?.schedulePlan ?? ""));
      setLoadingRecommendation(false);
    };
    onGenerate();
  }, [form]);

  if (loadingRecommendation) {
    return (
      <div className="container-releases-schedule-only-quincy loading-screen">
        <div className="content">
          <div className="loading-screen-content">
            <QuincyLoadingIcon className="loading-screen-icon" aria-hidden />
            <p className="loading-screen-text">
              {t("pages.ReleasesSchedule.onlyQuincy.gerandoCronograma")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`container-releases-schedule-only-quincy ${isMobile ? "mobile" : ""}`}
    >
      <div className="content">
        <div className="header-page">
          <p className="title">
            {t("pages.ReleasesSchedule.onlyQuincy.title")}
          </p>
          <p className="subtitle">
            {t("pages.ReleasesSchedule.onlyQuincy.subtitle")}
          </p>
        </div>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            [FIELD_CURRENT]: initialRecommendation,
            [FIELD_SUGGESTED]: initialRecommendation,
          }}
        >
          <TextAreaQuincySchedule />
          <div className="footer-actions">
            <button
              type="button"
              className="botao-roxo"
              onClick={onSave}
              disabled={saving}
            >
              {saving && <LoadingOutlined />}
              {t("pages.ReleasesSchedule.onlyQuincy.salvar")}
              <RightOutlined />
            </button>
            <button
              type="button"
              className="botao-branco-vazado"
              onClick={() => history.push("/releases-schedules")}
              disabled={saving}
            >
              {t("pages.ReleasesSchedule.voltar")}
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default NewReleasesScheduleOnlyQuincy;
